import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Vehicle, ProgressStatus, VehicleClass } from '../types';
import { PROGRESS_STATUSES, PROGRESS_COLOURS, CLASS_COLOURS, classLabel } from '../types';
import { vehiclesApi } from '../api/vehicles';
import { tagsApi } from '../api/tags';
import { dealershipsApi } from '../api/dealerships';
import { vehicleImageSrc } from '../utils/helpers';
import VehicleModal from '../components/VehicleModal';

const COL_STYLE = PROGRESS_COLOURS;

const CLASS_GROUPS: { label: string; classes: VehicleClass[] }[] = [
  { label: 'Cars',   classes: ['S', 'A', 'B', 'C', 'D'] },
  { label: 'Police', classes: ['Police'] },
  { label: 'Bikes',  classes: ['Bike-S', 'Bike-A', 'Bike-B', 'Bike-C', 'Bike-D'] },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Kanban() {
  const qc = useQueryClient();

  const [dragVehicleId, setDragVehicleId] = useState<number | null>(null);
  const [dragOverCol, setDragOverCol]     = useState<ProgressStatus | null>(null);
  const [classFilter, setClassFilter]     = useState<string[]>([]);
  const [selectedIds, setSelectedIds]     = useState<Set<number>>(new Set());
  const [search, setSearch]               = useState('');
  const [modalVehicle, setModalVehicle]   = useState<Vehicle | null>(null);

  const columnsRef = useRef<HTMLDivElement>(null);

  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles', {}],
    queryFn: () => vehiclesApi.list({}),
  });

  const { data: tagsData }        = useQuery({ queryKey: ['tags'],        queryFn: tagsApi.list });
  const { data: dealershipsData } = useQuery({ queryKey: ['dealerships'], queryFn: dealershipsApi.list });

  const vehicles         = vehiclesData?.vehicles ?? [];
  const filteredVehicles = vehicles
    .filter(v => !classFilter.length || classFilter.includes(v.vehicle_class))
    .filter(v => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (v.name ?? '').toLowerCase().includes(q) || v.spawncode.toLowerCase().includes(q);
    });

  // Single-card move
  const progressMutation = useMutation({
    mutationFn: ({ id, progress }: { id: number; progress: ProgressStatus }) =>
      vehiclesApi.patch(id, { progress }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  });

  // Bulk move
  const bulkMutation = useMutation({
    mutationFn: async (progress: ProgressStatus) => {
      await Promise.all([...selectedIds].map(id => vehiclesApi.patch(id, { progress })));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      setSelectedIds(new Set());
    },
  });

  useGSAP(() => {
    if (!columnsRef.current) return;
    const cols = columnsRef.current.querySelectorAll('.kb-col');
    gsap.from(cols, { y: 16, opacity: 0, stagger: 0.07, duration: 0.45, ease: 'power3.out' });
  }, []);

  // ── Drag handlers ────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: number) => {
    setDragVehicleId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('vehicleId', String(id));
    gsap.to(e.currentTarget, { scale: 1.03, opacity: 0.55, duration: 0.15 });
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setDragVehicleId(null);
    setDragOverCol(null);
    gsap.to(e.currentTarget, { scale: 1, opacity: 1, duration: 0.2 });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, status: ProgressStatus) => {
    e.preventDefault();
    const id = parseInt(e.dataTransfer.getData('vehicleId'));
    if (!id || isNaN(id)) return;

    // If the dragged card is part of a multi-selection, bulk-move the whole selection
    if (selectedIds.has(id) && selectedIds.size > 1) {
      bulkMutation.mutate(status);
    } else {
      const vehicle = vehicles.find(v => v.id === id);
      if (vehicle && vehicle.progress !== status) {
        progressMutation.mutate({ id, progress: status });
      }
    }

    setDragVehicleId(null);
    setDragOverCol(null);
  };

  // ── Selection ────────────────────────────────────────────────────
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const byStatus = (status: ProgressStatus) =>
    filteredVehicles
      .filter(v => v.progress === status)
      .sort((a, b) => (a.name ?? a.spawncode).localeCompare(b.name ?? b.spawncode));

  return (
    <div className="min-h-screen sakura-bg flex flex-col">

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="border-b px-6 py-3 flex items-center gap-4 flex-shrink-0 sticky top-0 z-30" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
        <h1 className="font-heading text-lg uppercase tracking-widest font-bold flex-shrink-0" style={{ color: 'var(--text)' }}>
          Kanban Board
        </h1>

        {/* Class filter pills */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* All pill — clears selection */}
          <button
            type="button"
            onClick={() => setClassFilter([])}
            style={{
              fontFamily: 'Outfit', fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              padding: '3px 10px',
              border: `1px solid ${classFilter.length === 0 ? 'var(--text)' : 'var(--border-strong)'}`,
              background: classFilter.length === 0 ? 'var(--text)' : 'transparent',
              color: classFilter.length === 0 ? 'var(--page)' : 'var(--text-muted)',
              cursor: 'pointer', transition: 'all 0.13s',
            }}
          >
            All
          </button>

          {CLASS_GROUPS.map((group) => (
            <div key={group.label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {/* Group divider */}
              <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 3px' }} />
              {group.classes.map(vc => {
                const col    = CLASS_COLOURS[vc];
                const active = classFilter.includes(vc);
                return (
                  <button
                    key={vc}
                    type="button"
                    onClick={() =>
                      setClassFilter(prev =>
                        prev.includes(vc) ? prev.filter(c => c !== vc) : [...prev, vc]
                      )
                    }
                    style={{
                      fontFamily: 'Outfit', fontSize: 10, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      padding: '3px 9px',
                      border: `1px solid ${active ? col.bg : 'var(--border-strong)'}`,
                      background: active ? col.bg : 'transparent',
                      color: active ? col.text : 'var(--text-muted)',
                      cursor: 'pointer', transition: 'all 0.13s',
                    }}
                  >
                    {classLabel(vc)}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <svg
            width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="var(--text-faint)" strokeWidth="2.5"
            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          >
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search vehicles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              fontFamily: 'Inter', fontSize: 12,
              paddingLeft: 26, paddingRight: search ? 24 : 10,
              paddingTop: 5, paddingBottom: 5,
              width: 180,
              background: 'var(--surface)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-faint)', fontSize: 14, lineHeight: 1, padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* Multi-select status — drag any selected card to bulk move */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 border-l pl-4" style={{ borderColor: 'var(--border)' }}>
            <span className="font-body text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
              <span className="font-heading" style={{ color: 'var(--text)' }}>{selectedIds.size}</span> selected
              {bulkMutation.isPending
                ? <span className="ml-2 text-primary">Moving…</span>
                : <span className="ml-2 opacity-60">— drag any selected card to move all</span>
              }
            </span>
            <button
              onClick={clearSelection}
              className="text-xs font-heading uppercase hover:text-primary transition-colors flex-shrink-0"
              style={{ color: 'var(--text-muted)' }}
            >
              Clear
            </button>
          </div>
        )}

        <span className="font-body text-xs ml-auto flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
          {filteredVehicles.length} vehicles — click to select · double-click to edit · drag to move
        </span>
      </div>

      {/* ── Columns — fill full viewport width, no horizontal scroll ── */}
      <div
        ref={columnsRef}
        style={{
          display: 'flex',
          gap: 8,
          padding: 12,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',   // no horizontal scroll — columns share the full width
          boxSizing: 'border-box',
        }}
      >
        {PROGRESS_STATUSES.map(status => {
          const cards  = byStatus(status);
          const col    = COL_STYLE[status];
          const isOver = dragOverCol === status;

          return (
            <div
              key={status}
              className="kb-col"
              onDragOver={e => { e.preventDefault(); if (dragOverCol !== status) setDragOverCol(status); }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={e => handleDrop(e, status)}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--card)',
                border: `1px solid ${isOver ? col.bg : 'var(--border)'}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'border-color 0.15s',
                overflow: 'hidden',
              }}
            >
              {/* Column header */}
              <div style={{
                background: col.bg,
                color: col.text,
                padding: '8px 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}>
                <span style={{ fontFamily: 'Outfit', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {col.label}
                </span>
                <span style={{ fontFamily: 'Outfit', fontSize: 11, fontWeight: 700, opacity: 0.65 }}>
                  {cards.length}
                </span>
              </div>

              {/* Card area — up to 3 cards wide, scrollable vertically */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: 6,
                  background: 'var(--surface)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gridAutoRows: 'max-content',
                  gap: 5,
                  alignContent: 'start',
                }}
              >
                {cards.map(v => (
                  <KanbanCard
                    key={v.id}
                    vehicle={v}
                    isDragging={dragVehicleId === v.id}
                    isSelected={selectedIds.has(v.id)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onSelect={toggleSelect}
                    onDoubleClick={setModalVehicle}
                  />
                ))}

                {/* Empty drop zone */}
                {cards.length === 0 && (
                  <div style={{
                    gridColumn: '1 / -1',
                    minHeight: 60,
                    border: `2px dashed ${isOver ? col.bg : 'var(--border)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'border-color 0.15s',
                  }}>
                    <span style={{ fontFamily: 'Inter', fontSize: 10, color: 'var(--text-faint)' }}>
                      Drop here
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modalVehicle && (
        <VehicleModal
          vehicle={modalVehicle}
          onClose={() => setModalVehicle(null)}
          tags={tagsData?.tags ?? []}
          dealerships={dealershipsData?.dealerships ?? []}
        />
      )}
    </div>
  );
}

// ─── Kanban Card ──────────────────────────────────────────────────────────────

interface KanbanCardProps {
  vehicle: Vehicle;
  isDragging: boolean;
  isSelected: boolean;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, id: number) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onSelect: (id: number) => void;
  onDoubleClick: (vehicle: Vehicle) => void;
}

function KanbanCard({ vehicle, isDragging, isSelected, onDragStart, onDragEnd, onSelect, onDoubleClick }: KanbanCardProps) {
  const img  = vehicleImageSrc(vehicle);
  const name = vehicle.name ?? vehicle.spawncode;
  const col  = CLASS_COLOURS[vehicle.vehicle_class] ?? { bg: '#374151', text: '#FFFFFF' };

  return (
    <div
      data-vehicle={vehicle.id}
      draggable
      onDragStart={e => onDragStart(e, vehicle.id)}
      onDragEnd={onDragEnd}
      onClick={() => onSelect(vehicle.id)}
      onDoubleClick={e => { e.stopPropagation(); onDoubleClick(vehicle); }}
      style={{
        background: 'var(--card)',
        border: isSelected
          ? `2px solid ${col.bg}`
          : '1px solid var(--border-strong)',
        boxShadow: isSelected
          ? `0 0 0 3px ${col.bg}22`
          : '0 1px 4px rgba(0,0,0,0.1)',
        cursor: 'grab',
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity 0.15s, border-color 0.13s, box-shadow 0.13s',
        userSelect: 'none',
        willChange: 'transform',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Selection tick — top-right corner */}
      <div style={{
        position: 'absolute',
        top: 4, right: 4,
        width: 13, height: 13,
        background: isSelected ? col.bg : 'var(--card)',
        border: `1.5px solid ${isSelected ? col.bg : 'var(--border-strong)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        transition: 'all 0.13s',
        flexShrink: 0,
      }}>
        {isSelected && (
          <span style={{ color: col.text, fontSize: 8, lineHeight: 1, fontWeight: 900 }}>✓</span>
        )}
      </div>

      {/* Name + class badge strip */}
      <div style={{
        padding: '5px 8px 5px 6px',
        background: 'var(--surface)',
        borderBottom: `2px solid ${col.bg}`,
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        minHeight: 28,
      }}>
        <span style={{
          fontFamily: 'Outfit',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          lineHeight: 1.2,
        }}>
          {name}
        </span>
        <span style={{
          fontFamily: 'Outfit',
          fontSize: 7,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          background: col.bg,
          color: col.text,
          padding: '1px 4px',
          flexShrink: 0,
        }}>
          {classLabel(vehicle.vehicle_class)}
        </span>
      </div>

      {/* Image */}
      <div style={{
        height: 64,
        width: '100%',
        background: `${col.bg}10`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {img ? (
          <img
            src={img}
            alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            loading="lazy"
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={col.bg} strokeWidth="1.5" opacity={0.3}>
              <rect x="2" y="7" width="20" height="12" rx="0"/>
              <path d="M16 7l-2-4H10L8 7"/>
              <circle cx="7" cy="16" r="2"/>
              <circle cx="17" cy="16" r="2"/>
            </svg>
            <span style={{ fontFamily: 'Inter', fontSize: 7, color: '#BBBBBB', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              No Image
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
